import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useState } from "react";
import { VIDEO } from "@/lib/config";

export default function VideoBlock() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      className="pt-6 sm:pt-10 pb-10 sm:pb-14"
      id="explainer"
      data-testid="video-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10"
        >
          <div className="lg:col-span-7">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              A tiny glimpse of what your <br />
              <span className="italic">next 21 mornings</span> look like.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="font-sans text-base sm:text-lg text-ink/75 leading-relaxed">
              A short film about the small, everyday moments this cohort will
              turn into memories — for both you and your child.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative aspect-[16/9] w-full overflow-hidden border border-ink/15"
          data-testid="video-frame"
        >
          {!playing ? (
            <>
              <img
                src={VIDEO.poster}
                alt="Wonder Time explainer video thumbnail"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = `https://img.youtube.com/vi/${VIDEO.youtubeId}/hqdefault.jpg`;
                }}
              />
              <div className="absolute inset-0 bg-ink/25" />
              <button
                onClick={() => setPlaying(true)}
                className="absolute inset-0 flex items-center justify-center group"
                data-testid="video-play-btn"
                aria-label="Play explainer video"
              >
                <span className="relative flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-cream/85 backdrop-blur-md border border-cream/40 transition-transform duration-500 group-hover:scale-110">
                  <Play
                    className="w-8 h-8 sm:w-10 sm:h-10 text-ink translate-x-0.5"
                    fill="currentColor"
                  />
                  <span className="absolute inset-0 rounded-full border border-cream/60 animate-ping opacity-60" />
                </span>
              </button>
              <div className="absolute bottom-6 left-6 text-cream font-serif italic text-lg sm:text-2xl max-w-md">
                {`"Ten minutes changed the shape of our whole day."`}
                <div className="mt-1 not-italic text-xs uppercase tracking-[0.2em] text-cream/70">
                  — a Wonder Time parent
                </div>
              </div>
            </>
          ) : (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={VIDEO.embedUrl}
              title="Wonder Time explainer video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-iframe"
            />
          )}
        </motion.div>
      </div>
    </section>
  );
}
